/**
 * GlobalOfflineSync.js
 * Modeled after Stocky's globalOfflineSync.js architecture.
 * Continuously monitors connectivity, performs pre-flight ping reachability checks,
 * idempotently batches pending offline orders to the cloud backend, and notifies the UI.
 */

import ApiClient from "./ApiClient";
import OfflineQueue from "./OfflineQueue";
import toast from "react-hot-toast";

let listenersAttached = false;
let syncInProgress = false;
let pollTimer = null;

/**
 * Ping backend to confirm genuine internet & API reachability
 */
export async function pingBackend() {
  try {
    const res = await ApiClient.get("/ping", { timeout: 2500 });
    return res && res.status === 200 && (res.data?.ok === true || res.data?.status === "ok");
  } catch (e) {
    return false;
  }
}

/**
 * Core synchronization routine
 */
export async function syncOfflineQueue() {
  if (syncInProgress) return { syncedCount: 0, lastError: null };

  // 1. Browser online signal check
  if (typeof window !== "undefined" && window.navigator && window.navigator.onLine === false) {
    return { syncedCount: 0, lastError: "Device is offline" };
  }

  // 2. Check if there are any orders to sync
  const pendingCount = OfflineQueue.getPendingCount();
  if (pendingCount === 0) {
    return { syncedCount: 0, lastError: null };
  }

  // 3. Pre-flight reachability ping
  const isReachable = await pingBackend();
  if (!isReachable) {
    return { syncedCount: 0, lastError: "Server unreachable" };
  }

  syncInProgress = true;
  try {
    window.dispatchEvent(new CustomEvent("restro_offline_sync_started"));
  } catch (e) {}

  let syncedCount = 0;
  let lastError = null;

  try {
    const queue = OfflineQueue.getOfflineOrders() || [];

    for (let i = 0; i < queue.length; i++) {
      const order = queue[i];
      if (!order || order.status === "synced" || order.status === "syncing") continue;

      OfflineQueue.markOrderAsSyncing(order.id);
      const payload = order.payload || {};

      try {
        let response;
        if (order.actionType === "ORDER") {
          response = await ApiClient.post("/pos/create-order", payload);
        } else if (order.actionType === "ORDER_AND_INVOICE") {
          response = await ApiClient.post("/pos/create-order-and-invoice", payload);
        } else if (order.actionType === "PAY_AND_COMPLETE") {
          response = await ApiClient.post("/orders/complete-and-pay-order", payload);
        } else {
          // Default fallback
          response = await ApiClient.post("/pos/create-order-and-invoice", payload);
        }

        if (response && (response.status === 200 || response.status === 201)) {
          OfflineQueue.markOrderAsSynced(order.id, response.data);
          syncedCount++;
        } else {
          OfflineQueue.markOrderAsFailed(order.id, "Unexpected server response", response?.status);
        }
      } catch (err) {
        const isNetworkError = !err.response || err.message === "Network Error" || err.code === "ECONNABORTED";
        if (isNetworkError) {
          lastError = "Network connection lost during sync";
          // Leave status as pending for next heartbeat attempt
          OfflineQueue._updateOrder(order.id, () => ({ status: "pending" }));
          break; // Stop loop until connection stabilizes
        } else {
          const errMsg = err.response?.data?.message || err.message || "Sync validation failed";
          OfflineQueue.markOrderAsFailed(order.id, errMsg, err.response?.status);
          lastError = errMsg;
        }
      }
    }

    // Prune completed orders
    OfflineQueue.pruneSyncedOrders();

    if (syncedCount > 0) {
      toast.success(`Successfully synchronized ${syncedCount} offline order${syncedCount > 1 ? "s" : ""}!`);
    }
  } catch (err) {
    console.error("[GlobalOfflineSync] Error during sync:", err);
    lastError = err.message;
  } finally {
    syncInProgress = false;
    try {
      window.dispatchEvent(
        new CustomEvent("restro_offline_sync_completed", {
          detail: { syncedCount, lastError }
        })
      );
    } catch (e) {}
  }

  return { syncedCount, lastError };
}

/**
 * Public initializer: attaches listeners and starts the 5s background heartbeat
 */
export function setupGlobalOfflineSync() {
  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;

  const maybeKickSync = () => {
    try {
      if (window.navigator && window.navigator.onLine === false) return;
      if (OfflineQueue.getPendingCount() === 0) return;
      syncOfflineQueue();
    } catch (e) {}
  };

  try {
    window.addEventListener("online", maybeKickSync);
    window.addEventListener("focus", maybeKickSync);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        maybeKickSync();
      }
    });
  } catch (e) {}

  // Run initial check
  setTimeout(maybeKickSync, 2000);

  // 5-second polling heartbeat
  if (!pollTimer) {
    pollTimer = setInterval(maybeKickSync, 5000);
  }
}

export default {
  pingBackend,
  syncOfflineQueue,
  setupGlobalOfflineSync
};
